/**
 * Browser-side host for the chores Tier-2 wasm module
 * (internal/core/wasm). Mirrors iOS's WasmHost.swift call shape so
 * a future shared TypeScript port stays cheap: JSON in → JSON out
 * via a (ptr, len) ABI, with `ptr<<32 | len` packed into an i64
 * return.
 *
 * ABI is documented in ~/Dev/elka/spec/wasm-abi.md §Function export
 * shape.
 */

import { createWasiImports } from './wasi-shim';

export class WasmHostError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(cause instanceof Error ? `${message}: ${cause.message}` : message);
    this.name = 'WasmHostError';
  }
}

/**
 * WasmHost loads the chores Tier-2 module once and serves many
 * `invoke` calls. Build expense (compile + instantiate) is paid up
 * front; per-call overhead is bounded by JSON marshal + a memcpy
 * in/out.
 */
export class WasmHost {
  private constructor(
    private readonly instance: WebAssembly.Instance,
    private readonly memory: WebAssembly.Memory,
    private readonly malloc: (size: number) => number,
    private readonly release: ((ptr: number) => void) | null,
  ) {}

  /**
   * Load + instantiate from a streaming fetch response. Use this
   * in the browser; the response's MIME type must be
   * `application/wasm` for streaming compile (Node lacks that
   * codepath, hence `loadFromBytes`).
   */
  static async loadFromUrl(url: string): Promise<WasmHost> {
    let response: Response;
    try {
      response = await fetch(url);
    } catch (e) {
      throw new WasmHostError(`fetch ${url}`, e);
    }
    if (!response.ok) {
      throw new WasmHostError(`fetch ${url}: HTTP ${response.status}`);
    }
    const bytes = new Uint8Array(await response.arrayBuffer());
    return WasmHost.loadFromBytes(bytes);
  }

  /**
   * Load from raw bytes. Used by tests and by the Node-side wasm
   * smoke (no streaming compile).
   */
  static async loadFromBytes(bytes: Uint8Array): Promise<WasmHost> {
    let memoryRef: WebAssembly.Memory | null = null;
    const wasi = createWasiImports(() => {
      if (!memoryRef) throw new WasmHostError('memory accessed before instantiation');
      return memoryRef;
    });
    let instance: WebAssembly.Instance;
    try {
      // Two-step compile+instantiate so the overload picks the
      // (Module, imports) -> Promise<Instance> shape; passing raw
      // bytes selects the WebAssemblyInstantiatedSource overload
      // whose typing varies across TS dom lib versions.
      const module = await WebAssembly.compile(bytes as BufferSource);
      instance = await WebAssembly.instantiate(module, {
        wasi_snapshot_preview1: wasi as unknown as WebAssembly.ModuleImports,
      });
    } catch (e) {
      throw new WasmHostError('instantiate', e);
    }

    const exports = instance.exports as Record<string, unknown>;
    const memory = exports.memory as WebAssembly.Memory | undefined;
    if (!memory) throw new WasmHostError('guest does not export memory');
    memoryRef = memory;
    const malloc = exports.malloc as ((size: number) => number) | undefined;
    if (typeof malloc !== 'function') {
      throw new WasmHostError('guest does not export malloc');
    }
    const releaseRaw = exports.elka_release;
    const release: ((ptr: number) => void) | null =
      typeof releaseRaw === 'function' ? (releaseRaw as (ptr: number) => void) : null;

    // We deliberately do NOT call _start: TinyGo's `main()` for our
    // wasi build is a no-op, and _start proc_exits the module so
    // every subsequent export call would trap. Match iOS WasmHost.
    return new WasmHost(instance, memory, malloc, release);
  }

  /**
   * Invokes a guest entry point. Marshals `input` into a
   * guest-allocated buffer, calls (ptr, len), reads the packed
   * (ptr<<32 | len) return, copies the output bytes out, and (if
   * exported) calls elka_release on the output pointer.
   */
  invoke(name: string, input: Uint8Array): Uint8Array {
    const entry = (this.instance.exports as Record<string, unknown>)[name];
    if (typeof entry !== 'function') {
      throw new WasmHostError(`guest does not export ${name}`);
    }
    const inPtr = this.malloc(input.length);
    if (inPtr === 0) throw new WasmHostError('malloc returned 0');
    new Uint8Array(this.memory.buffer, inPtr, input.length).set(input);

    let packed: bigint;
    try {
      packed = (entry as (p: number, l: number) => bigint)(inPtr, input.length);
    } catch (e) {
      throw new WasmHostError(`${name} trap`, e);
    }
    // i64 return packs (ptr<<32 | len). The function signature is
    // declared as returning i64, so the JS-side value is a bigint.
    const outPtr = Number(packed >> 32n);
    const outLen = Number(packed & 0xffff_ffffn);

    const out = new Uint8Array(outLen);
    out.set(new Uint8Array(this.memory.buffer, outPtr, outLen));

    if (this.release) {
      try {
        this.release(outPtr);
      } catch {
        // Per iOS host: best-effort release; a failure here
        // shouldn't poison the caller.
      }
    }
    return out;
  }
}
